import React, { useEffect, useMemo, useState } from "react";
import {
  getPassiveLoadSchedules,
  createPassiveLoadSchedule,
  updatePassiveLoadSchedule,
  getPassiveLoadSettings,
  createPassiveLoadSettings,
  updatePassiveLoadSettings,
  getPassiveLoadExceptions,
  createPassiveLoadException,
  updatePassiveLoadException,
  deletePassiveLoadException,
} from "../../../redux/apiCalls/analysisCall/passiveLoadConfigCall";

const dayNames = [
  "Domingo",
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
];

const activityLevels = [
  { value: "", label: "Sin definir" },
  { value: "BAJO", label: "Bajo" },
  { value: "NORMAL", label: "Normal" },
  { value: "ALTO", label: "Alto" },
];

const exceptionTypes = [
  { value: "OVERTIME", label: "Hora extra autorizada" },
  { value: "SPECIAL_WORKING", label: "Horario especial laborable" },
  { value: "SPECIAL_CLOSED", label: "Cierre especial" },
  { value: "HOLIDAY", label: "Feriado / no laborable" },
];

const baseReferenceProfiles = {
  low: {
    label: "Base mínima habitual",
    description:
      "Toma una referencia más baja del consumo normal del dispositivo. Sirve para detectar consumos elevados con más sensibilidad.",
    percentile: 15,
  },
  recommended: {
    label: "Base recomendada",
    description:
      "Es el equilibrio sugerido. Usa una base baja habitual, pero evita tomar casos demasiado extremos.",
    percentile: 25,
  },
  stable: {
    label: "Base más estable",
    description:
      "Toma una referencia más amplia del comportamiento normal. Puede generar menos avisos, porque considera más consumos como normales.",
    percentile: 40,
  },
};

const baseToleranceProfiles = {
  strict: {
    label: "Margen ajustado",
    description:
      "Permite poca diferencia sobre la línea base. Detecta más consumos elevados.",
    multiplier: 1.1,
  },
  normal: {
    label: "Margen normal",
    description: "Margen recomendado para la mayoría de los casos.",
    multiplier: 1.2,
  },
  flexible: {
    label: "Margen amplio",
    description:
      "Permite más diferencia sobre la línea base. Detecta solo consumos más evidentes.",
    multiplier: 1.5,
  },
};

const sensitivityProfiles = {
  low: {
    label: "Baja - menos avisos",
    description: "Avisa solo cuando el consumo se aleja mucho de lo normal.",
    review: 1.5,
    anomaly: 2.5,
    critical: 3.5,
  },
  normal: {
    label: "Normal - recomendado",
    description: "Equilibrio recomendado entre detección y cantidad de avisos.",
    review: 1,
    anomaly: 2,
    critical: 3,
  },
  high: {
    label: "Alta - más avisos",
    description: "Avisa ante cambios más chicos en el consumo fuera de horario.",
    review: 0.7,
    anomaly: 1.5,
    critical: 2.5,
  },
};

const toBoolean = (value) =>
  value === true ||
  value === 1 ||
  value === "1" ||
  String(value).toLowerCase() === "true";

const normalizeTime = (value) => {
  if (!value) return "";
  return String(value).slice(0, 5);
};

const normalizeId = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const numberValue = Number(value);
  return Number.isNaN(numberValue) ? value : numberValue;
};

const numbersAreClose = (a, b) => Math.abs(Number(a) - Number(b)) < 0.001;

const getBaseReferenceProfileValue = (form) => {
  const percentile = Number(form.base_load_percentile);

  const entry = Object.entries(baseReferenceProfiles).find(([, profile]) =>
    numbersAreClose(percentile, profile.percentile)
  );

  return entry?.[0] || "custom";
};

const getBaseToleranceProfileValue = (form) => {
  const multiplier = Number(form.passive_threshold_multiplier);

  const entry = Object.entries(baseToleranceProfiles).find(([, profile]) =>
    numbersAreClose(multiplier, profile.multiplier)
  );

  return entry?.[0] || "custom";
};

const getSensitivityProfileValue = (form) => {
  const review = Number(form.zscore_review_threshold);
  const anomaly = Number(form.zscore_anomaly_threshold);
  const critical = Number(form.zscore_critical_threshold);

  const entry = Object.entries(sensitivityProfiles).find(([, profile]) => {
    return (
      numbersAreClose(review, profile.review) &&
      numbersAreClose(anomaly, profile.anomaly) &&
      numbersAreClose(critical, profile.critical)
    );
  });

  return entry?.[0] || "custom";
};

const getDeviceData = (device) => device?.data || device || {};

const getDeviceBranchId = (device) => {
  const data = getDeviceData(device);

  return normalizeId(
    device?.branch_fk ??
      device?.branch_id ??
      device?.branchId ??
      data?.branch_fk ??
      data?.branch_id ??
      data?.branchId ??
      null
  );
};

const getDeviceCompanyId = (device) => {
  const data = getDeviceData(device);

  return normalizeId(
    device?.company_fk ??
      device?.company_id ??
      device?.companyId ??
      data?.company_fk ??
      data?.company_id ??
      data?.companyId ??
      null
  );
};

const getDeviceMac = (device) => {
  const data = getDeviceData(device);

  return (
    device?.mac ||
    device?.serial_number ||
    data?.mac ||
    data?.serial_number ||
    null
  );
};

const getDeviceName = (device) => {
  const data = getDeviceData(device);

  return (
    device?.label ||
    device?.name ||
    data?.label ||
    data?.name ||
    getDeviceMac(device) ||
    "Dispositivo sin identificar"
  );
};

const getDeviceBranchLabel = (device) => {
  const data = getDeviceData(device);

  const branchName =
    data?.branch_office?.name ||
    data?.branchOffice?.name ||
    data?.branch_name ||
    data?.branchName ||
    device?.branch_name ||
    device?.branchName ||
    "";

  const branchId = getDeviceBranchId(device);

  if (branchName) return branchName;
  if (branchId != null) return `Sucursal ${branchId}`;

  return "Sucursal sin identificar";
};

const getDeviceCompanyLabel = (device) => {
  const data = getDeviceData(device);

  return (
    data?.company?.name ||
    data?.company_name ||
    data?.companyName ||
    device?.company_name ||
    device?.companyName ||
    "Empresa sin identificar"
  );
};

const createDefaultWeeklyGrid = () => [
  {
    day_of_week: 0,
    is_working_day: false,
    start_time: "",
    end_time: "",
    pre_operational_minutes: 0,
    post_operational_minutes: 0,
    expected_activity_level: "",
    description: "No laborable",
    enabled: true,
  },
  {
    day_of_week: 1,
    is_working_day: true,
    start_time: "08:00",
    end_time: "18:00",
    pre_operational_minutes: 60,
    post_operational_minutes: 60,
    expected_activity_level: "NORMAL",
    description: "Lunes laborable",
    enabled: true,
  },
  {
    day_of_week: 2,
    is_working_day: true,
    start_time: "08:00",
    end_time: "18:00",
    pre_operational_minutes: 60,
    post_operational_minutes: 60,
    expected_activity_level: "NORMAL",
    description: "Martes laborable",
    enabled: true,
  },
  {
    day_of_week: 3,
    is_working_day: true,
    start_time: "08:00",
    end_time: "18:00",
    pre_operational_minutes: 60,
    post_operational_minutes: 60,
    expected_activity_level: "NORMAL",
    description: "Miércoles laborable",
    enabled: true,
  },
  {
    day_of_week: 4,
    is_working_day: true,
    start_time: "08:00",
    end_time: "18:00",
    pre_operational_minutes: 60,
    post_operational_minutes: 60,
    expected_activity_level: "NORMAL",
    description: "Jueves laborable",
    enabled: true,
  },
  {
    day_of_week: 5,
    is_working_day: true,
    start_time: "08:00",
    end_time: "18:00",
    pre_operational_minutes: 60,
    post_operational_minutes: 60,
    expected_activity_level: "NORMAL",
    description: "Viernes laborable",
    enabled: true,
  },
  {
    day_of_week: 6,
    is_working_day: true,
    start_time: "08:00",
    end_time: "13:00",
    pre_operational_minutes: 30,
    post_operational_minutes: 30,
    expected_activity_level: "NORMAL",
    description: "Sábado laborable",
    enabled: true,
  },
];

const defaultSettingsForm = () => ({
  base_load_method: "percentile",
  base_load_percentile: 25,
  passive_threshold_multiplier: 1.2,

  zscore_review_threshold: 1,
  zscore_anomaly_threshold: 2,
  zscore_critical_threshold: 3,

  probability_review_threshold: 0.1,
  probability_anomaly_threshold: 0.05,
  probability_critical_threshold: 0.01,

  min_passive_points: 2,

  use_schedule: true,
  use_base_load_threshold: true,
  use_statistical_detection: true,
  enabled: true,
});

const createDefaultExceptionRow = () => ({
  id: null,
  exception_date: "",
  start_time: "18:00",
  end_time: "22:00",
  exception_type: "OVERTIME",
  description: "Hora extra autorizada",
  enabled: true,
});

const isScopedRow = (row, { mode, mac, branchId, companyId }) => {
  if (!row) return false;

  if (mode === "device") {
    return row.mac === mac;
  }

  const rowMacEmpty =
    row.mac === null || row.mac === undefined || row.mac === "";

  const branchMatches =
    branchId == null
      ? row.branch_id == null
      : Number(row.branch_id) === Number(branchId);

  const companyMatches =
    companyId == null
      ? true
      : row.company_id == null || Number(row.company_id) === Number(companyId);

  return rowMacEmpty && branchMatches && companyMatches;
};

const buildSchedulePayload = (row, scope) => ({
  company_id: scope.companyId ?? null,
  branch_id: scope.branchId ?? null,
  mac: scope.mode === "device" ? scope.mac : null,
  day_of_week: Number(row.day_of_week),
  start_time: row.is_working_day ? row.start_time || null : null,
  end_time: row.is_working_day ? row.end_time || null : null,
  is_working_day: Boolean(row.is_working_day),
  pre_operational_minutes: Number(row.pre_operational_minutes) || 0,
  post_operational_minutes: Number(row.post_operational_minutes) || 0,
  expected_activity_level: row.expected_activity_level || null,
  description: row.description || null,
  enabled: Boolean(row.enabled),
});

const buildSettingsPayload = (form, scope) => ({
  company_id: scope.companyId ?? null,
  branch_id: scope.branchId ?? null,
  mac: scope.mode === "device" ? scope.mac : null,

  base_load_method: form.base_load_method || "percentile",
  base_load_percentile: Number(form.base_load_percentile),
  passive_threshold_multiplier: Number(form.passive_threshold_multiplier),

  zscore_review_threshold: Number(form.zscore_review_threshold),
  zscore_anomaly_threshold: Number(form.zscore_anomaly_threshold),
  zscore_critical_threshold: Number(form.zscore_critical_threshold),

  probability_review_threshold: Number(form.probability_review_threshold),
  probability_anomaly_threshold: Number(form.probability_anomaly_threshold),
  probability_critical_threshold: Number(form.probability_critical_threshold),

  min_passive_points: Number(form.min_passive_points),

  use_schedule: Boolean(form.use_schedule),
  use_base_load_threshold: Boolean(form.use_base_load_threshold),
  use_statistical_detection: Boolean(form.use_statistical_detection),
  enabled: Boolean(form.enabled),
});

const buildExceptionPayload = (row, scope) => ({
  company_id: scope.companyId ?? null,
  branch_id: scope.branchId ?? null,
  mac: scope.mode === "device" ? scope.mac : null,
  exception_date: row.exception_date || null,
  start_time: normalizeTime(row.start_time) || null,
  end_time: normalizeTime(row.end_time) || null,
  exception_type: row.exception_type
    ? String(row.exception_type).toUpperCase()
    : "OVERTIME",
  description: row.description || null,
  enabled: Boolean(row.enabled),
});

const PassiveLoadConfig = ({ token, devices = [], onClose }) => {
  const [activeTab, setActiveTab] = useState("schedule");

  const [scheduleGrid, setScheduleGrid] = useState(createDefaultWeeklyGrid());
  const [settingsForm, setSettingsForm] = useState(defaultSettingsForm());
  const [exceptions, setExceptions] = useState([]);

  const [loading, setLoading] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingExceptions, setSavingExceptions] = useState(false);

  const [deviceSpecificMode, setDeviceSpecificMode] = useState(false);
  const [deviceModeTouched, setDeviceModeTouched] = useState(false);

  const [activeBranchKey, setActiveBranchKey] = useState(null);
  const [activeDeviceMac, setActiveDeviceMac] = useState(null);
  const [message, setMessage] = useState(null);

  const normalizedDevices = useMemo(() => {
    return (devices || [])
      .map((device) => {
        const mac = getDeviceMac(device);
        const branchId = getDeviceBranchId(device);
        const companyId = getDeviceCompanyId(device);

        return {
          raw: device,
          mac,
          branchId,
          companyId,
          deviceLabel: getDeviceName(device),
          branchLabel: getDeviceBranchLabel(device),
          companyLabel: getDeviceCompanyLabel(device),
        };
      })
      .filter((device) => device.mac || device.branchId != null);
  }, [devices]);

  useEffect(() => {
    if (!normalizedDevices.length) return;

    if (!deviceModeTouched) {
      setDeviceSpecificMode(normalizedDevices.length === 1);
    }
  }, [normalizedDevices.length, deviceModeTouched]);

  const branches = useMemo(() => {
    const map = new Map();

    normalizedDevices.forEach((device) => {
      const key = `${device.companyId ?? "__NULL__"}|${
        device.branchId ?? "__NULL__"
      }`;

      if (!map.has(key)) {
        map.set(key, {
          key,
          branch_id: device.branchId,
          company_id: device.companyId,
          label: device.branchLabel,
          companyLabel: device.companyLabel,
          devices: [],
        });
      }

      map.get(key).devices.push(device);
    });

    return Array.from(map.values());
  }, [normalizedDevices]);

  const activeBranch = useMemo(() => {
    return (
      branches.find((branch) => branch.key === activeBranchKey) ||
      branches[0] ||
      null
    );
  }, [branches, activeBranchKey]);

  const activeDevice = useMemo(() => {
    const branchDevices = activeBranch?.devices || normalizedDevices;

    return (
      branchDevices.find((device) => device.mac === activeDeviceMac) ||
      branchDevices[0] ||
      normalizedDevices[0] ||
      null
    );
  }, [activeBranch, activeDeviceMac, normalizedDevices]);

  const scope = useMemo(() => {
    if (deviceSpecificMode) {
      return {
        mode: "device",
        mac: activeDevice?.mac || null,
        branchId: activeDevice?.branchId ?? activeBranch?.branch_id ?? null,
        companyId: activeDevice?.companyId ?? activeBranch?.company_id ?? null,
      };
    }

    return {
      mode: "branch",
      mac: null,
      branchId: activeBranch?.branch_id ?? null,
      companyId: activeBranch?.company_id ?? null,
    };
  }, [deviceSpecificMode, activeDevice, activeBranch]);

  const baseReferenceValue = useMemo(() => {
    return getBaseReferenceProfileValue(settingsForm);
  }, [settingsForm]);

  const baseToleranceValue = useMemo(() => {
    return getBaseToleranceProfileValue(settingsForm);
  }, [settingsForm]);

  const sensitivityProfileValue = useMemo(() => {
    return getSensitivityProfileValue(settingsForm);
  }, [settingsForm]);

  useEffect(() => {
    if (!branches.length) {
      setActiveBranchKey(null);
      return;
    }

    setActiveBranchKey((current) => {
      if (branches.some((branch) => branch.key === current)) return current;
      return branches[0].key;
    });
  }, [branches]);

  useEffect(() => {
    if (!normalizedDevices.length) {
      setActiveDeviceMac(null);
      return;
    }

    setActiveDeviceMac((current) => {
      if (normalizedDevices.some((device) => device.mac === current)) {
        return current;
      }

      return normalizedDevices[0].mac || null;
    });
  }, [normalizedDevices]);

  useEffect(() => {
    if (!token) return;

    if (scope.mode === "branch" && scope.branchId == null) {
      setScheduleGrid(createDefaultWeeklyGrid());
      setSettingsForm(defaultSettingsForm());
      setExceptions([]);
      return;
    }

    if (scope.mode === "device" && !scope.mac) {
      setScheduleGrid(createDefaultWeeklyGrid());
      setSettingsForm(defaultSettingsForm());
      setExceptions([]);
      return;
    }

    loadConfiguration(scope);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, scope.mode, scope.mac, scope.branchId, scope.companyId]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
  };

  const getScopeFilters = (scopeValue = scope) => {
    if (scopeValue.mode === "device") {
      return { mac: scopeValue.mac };
    }

    return {
      branch_id: scopeValue.branchId,
      ...(scopeValue.companyId != null
        ? { company_id: scopeValue.companyId }
        : {}),
    };
  };

  const getBranchFilters = (scopeValue = scope) => ({
    branch_id: scopeValue.branchId,
    ...(scopeValue.companyId != null
      ? { company_id: scopeValue.companyId }
      : {}),
  });

  const getCompanyFilters = (scopeValue = scope) => ({
    company_id: scopeValue.companyId,
  });

  const validateScope = (scopeValue = scope) => {
    if (scopeValue.mode === "device") {
      if (!scopeValue.mac) {
        return "No se pudo identificar el dispositivo seleccionado.";
      }

      return null;
    }

    if (scopeValue.branchId == null) {
      return "No se pudo identificar la sucursal del dispositivo seleccionado.";
    }

    return null;
  };

  const buildWeeklyGrid = (rows = []) => {
    const base = createDefaultWeeklyGrid();

    return base.map((row) => {
      const existing = rows.find(
        (item) => Number(item.day_of_week) === Number(row.day_of_week)
      );

      if (!existing) return row;

      return {
        ...row,
        id: existing.id,
        mac: existing.mac,
        company_id: existing.company_id,
        branch_id: existing.branch_id,
        is_working_day: toBoolean(existing.is_working_day),
        start_time: normalizeTime(existing.start_time),
        end_time: normalizeTime(existing.end_time),
        pre_operational_minutes:
          existing.pre_operational_minutes ?? row.pre_operational_minutes,
        post_operational_minutes:
          existing.post_operational_minutes ?? row.post_operational_minutes,
        expected_activity_level:
          existing.expected_activity_level || row.expected_activity_level,
        description: existing.description || row.description,
        enabled:
          existing.enabled === undefined || existing.enabled === null
            ? row.enabled
            : toBoolean(existing.enabled),
      };
    });
  };

  const normalizeSettings = (row) => ({
    ...defaultSettingsForm(),
    ...row,
    base_load_percentile: Number(row.base_load_percentile ?? 25),
    passive_threshold_multiplier: Number(
      row.passive_threshold_multiplier ?? 1.2
    ),
    zscore_review_threshold: Number(row.zscore_review_threshold ?? 1),
    zscore_anomaly_threshold: Number(row.zscore_anomaly_threshold ?? 2),
    zscore_critical_threshold: Number(row.zscore_critical_threshold ?? 3),
    probability_review_threshold: Number(row.probability_review_threshold ?? 0.1),
    probability_anomaly_threshold: Number(
      row.probability_anomaly_threshold ?? 0.05
    ),
    probability_critical_threshold: Number(
      row.probability_critical_threshold ?? 0.01
    ),
    min_passive_points: Number(row.min_passive_points ?? 2),
    use_schedule: toBoolean(row.use_schedule),
    use_base_load_threshold: toBoolean(row.use_base_load_threshold),
    use_statistical_detection: toBoolean(row.use_statistical_detection),
    enabled: toBoolean(row.enabled),
  });

  const normalizeExceptions = (rows = []) => {
    return rows.map((row) => ({
      ...row,
      exception_date: row.exception_date
        ? String(row.exception_date).slice(0, 10)
        : "",
      start_time: normalizeTime(row.start_time || "18:00"),
      end_time: normalizeTime(row.end_time || "22:00"),
      exception_type: row.exception_type
        ? String(row.exception_type).toUpperCase()
        : "OVERTIME",
      description: row.description || "",
      enabled:
        row.enabled === undefined || row.enabled === null
          ? true
          : toBoolean(row.enabled),
    }));
  };

  const filterRowsByScope = (rows, scopeValue = scope) => {
    return (Array.isArray(rows) ? rows : []).filter((row) =>
      isScopedRow(row, scopeValue)
    );
  };

  const branchScopeFrom = (scopeValue) => ({
    mode: "branch",
    mac: null,
    branchId: scopeValue.branchId,
    companyId: scopeValue.companyId,
  });

  const companyScopeFrom = (scopeValue) => ({
    mode: "branch",
    mac: null,
    branchId: null,
    companyId: scopeValue.companyId,
  });

  const loadScopedRowsWithFallback = async ({
    scopeToLoad,
    fetcher,
    fallbackEnabled = true,
  }) => {
    const mainFilters = getScopeFilters(scopeToLoad);
    const mainRowsRaw = await fetcher(token, mainFilters);
    const mainRows = filterRowsByScope(mainRowsRaw, scopeToLoad);

    if (mainRows.length > 0 || !fallbackEnabled) {
      return {
        rows: mainRows,
        source: scopeToLoad.mode === "device" ? "device" : "branch",
      };
    }

    if (scopeToLoad.mode === "device" && scopeToLoad.branchId != null) {
      const branchScope = branchScopeFrom(scopeToLoad);
      const branchRowsRaw = await fetcher(token, getBranchFilters(scopeToLoad));
      const branchRows = filterRowsByScope(branchRowsRaw, branchScope);

      if (branchRows.length > 0) {
        return {
          rows: branchRows,
          source: "branch",
        };
      }
    }

    if (scopeToLoad.companyId != null) {
      const companyScope = companyScopeFrom(scopeToLoad);
      const companyRowsRaw = await fetcher(token, getCompanyFilters(scopeToLoad));
      const companyRows = filterRowsByScope(companyRowsRaw, companyScope);

      if (companyRows.length > 0) {
        return {
          rows: companyRows,
          source: "company",
        };
      }
    }

    return {
      rows: [],
      source: "default",
    };
  };

  const getFallbackMessage = (scopeToLoad, sources) => {
    if (scopeToLoad.mode !== "device") return null;

    if (
      sources.schedule === "device" ||
      sources.settings === "device" ||
      sources.exceptions === "device"
    ) {
      return "Se está usando la configuración específica de este dispositivo.";
    }

    if (
      sources.schedule === "branch" ||
      sources.settings === "branch" ||
      sources.exceptions === "branch"
    ) {
      return "Este dispositivo no tiene configuración propia. Se autocompletó con la configuración de la sucursal. Si guardás, se creará una configuración específica para este dispositivo.";
    }

    if (
      sources.schedule === "company" ||
      sources.settings === "company" ||
      sources.exceptions === "company"
    ) {
      return "Este dispositivo no tiene configuración propia ni de sucursal. Se autocompletó con la configuración de la empresa. Si guardás, se creará una configuración específica para este dispositivo.";
    }

    return null;
  };

  const loadConfiguration = async (scopeToLoad) => {
    setLoading(true);
    setMessage(null);

    try {
      const [schedulesResult, settingsResult, exceptionsResult] =
        await Promise.all([
          loadScopedRowsWithFallback({
            scopeToLoad,
            fetcher: getPassiveLoadSchedules,
            fallbackEnabled: true,
          }),
          loadScopedRowsWithFallback({
            scopeToLoad,
            fetcher: getPassiveLoadSettings,
            fallbackEnabled: true,
          }),
          loadScopedRowsWithFallback({
            scopeToLoad,
            fetcher: getPassiveLoadExceptions,
            fallbackEnabled: true,
          }),
        ]);

      setScheduleGrid(buildWeeklyGrid(schedulesResult.rows));
      setExceptions(normalizeExceptions(exceptionsResult.rows));

      if (settingsResult.rows.length > 0) {
        setSettingsForm(normalizeSettings(settingsResult.rows[0]));
      } else {
        setSettingsForm(defaultSettingsForm());
      }

      const fallbackMessage = getFallbackMessage(scopeToLoad, {
        schedule: schedulesResult.source,
        settings: settingsResult.source,
        exceptions: exceptionsResult.source,
      });

      if (fallbackMessage) {
        showMessage("info", fallbackMessage);
      } else if (!schedulesResult.rows.length) {
        showMessage(
          "info",
          scopeToLoad.mode === "branch"
            ? "No hay horario configurado para esta sucursal. Se usará el calendario por defecto hasta que guardes una configuración."
            : "No hay horario específico para este dispositivo. Se usará la configuración de sucursal/empresa o el calendario por defecto."
        );
      }
    } catch (error) {
      console.error("Error cargando configuración de carga pasiva:", error);
      showMessage("error", "No se pudo cargar la configuración.");
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleChange = (dayOfWeek, field, value) => {
    setScheduleGrid((current) =>
      current.map((row) => {
        if (Number(row.day_of_week) !== Number(dayOfWeek)) return row;

        if (field === "is_working_day") {
          const checked = Boolean(value);

          return {
            ...row,
            is_working_day: checked,
            start_time: checked ? row.start_time || "08:00" : "",
            end_time: checked ? row.end_time || "18:00" : "",
            description: checked
              ? row.description || `${dayNames[row.day_of_week]} laborable`
              : "No laborable",
          };
        }

        if (field === "enabled") {
          return {
            ...row,
            enabled: Boolean(value),
          };
        }

        return {
          ...row,
          [field]: value,
        };
      })
    );
  };

  const handleSettingsChange = (field, value) => {
    setSettingsForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleBaseReferenceChange = (profileKey) => {
    const profile = baseReferenceProfiles[profileKey];
    if (!profile) return;

    setSettingsForm((current) => ({
      ...current,
      base_load_method: "percentile",
      base_load_percentile: profile.percentile,
    }));
  };

  const handleBaseToleranceChange = (profileKey) => {
    const profile = baseToleranceProfiles[profileKey];
    if (!profile) return;

    setSettingsForm((current) => ({
      ...current,
      passive_threshold_multiplier: profile.multiplier,
    }));
  };

  const handleSensitivityProfileChange = (profileKey) => {
    const profile = sensitivityProfiles[profileKey];
    if (!profile) return;

    setSettingsForm((current) => ({
      ...current,
      zscore_review_threshold: profile.review,
      zscore_anomaly_threshold: profile.anomaly,
      zscore_critical_threshold: profile.critical,
    }));
  };

  const handleExceptionChange = (index, field, value) => {
    setExceptions((current) =>
      current.map((row, rowIndex) =>
        rowIndex !== index
          ? row
          : {
              ...row,
              [field]:
                field === "exception_type"
                  ? String(value).toUpperCase()
                  : value,
            }
      )
    );
  };

  const loadStandardSchedule = () => {
    setScheduleGrid(createDefaultWeeklyGrid());
    showMessage(
      "info",
      "Horario estándar cargado. Recordá guardar para aplicar los cambios."
    );
  };

  const loadDefaultSettings = () => {
    setSettingsForm(defaultSettingsForm());
    showMessage(
      "info",
      "Parámetros recomendados cargados. Recordá guardar para aplicar los cambios."
    );
  };

  const validateSchedule = () => {
    for (const row of scheduleGrid) {
      const day = Number(row.day_of_week);

      if (day < 0 || day > 6) {
        return "Hay un día inválido en el horario operativo.";
      }

      if (row.is_working_day && (!row.start_time || !row.end_time)) {
        return `El día ${dayNames[day]} está marcado como laborable y necesita hora de inicio y fin.`;
      }

      if (Number(row.pre_operational_minutes) < 0) {
        return `El pre-operativo de ${dayNames[day]} no puede ser negativo.`;
      }

      if (Number(row.post_operational_minutes) < 0) {
        return `El post-operativo de ${dayNames[day]} no puede ser negativo.`;
      }
    }

    return null;
  };

  const validateSettings = () => {
    const basePercentile = Number(settingsForm.base_load_percentile);
    const multiplier = Number(settingsForm.passive_threshold_multiplier);

    const zReview = Number(settingsForm.zscore_review_threshold);
    const zAnomaly = Number(settingsForm.zscore_anomaly_threshold);
    const zCritical = Number(settingsForm.zscore_critical_threshold);

    if (
      !Number.isFinite(basePercentile) ||
      basePercentile < 1 ||
      basePercentile > 99
    ) {
      return "La referencia de carga base tiene un valor inválido.";
    }

    if (!Number.isFinite(multiplier) || multiplier <= 0) {
      return "El margen de carga base tiene un valor inválido.";
    }

    if (
      !Number.isFinite(zReview) ||
      !Number.isFinite(zAnomaly) ||
      !Number.isFinite(zCritical)
    ) {
      return "La sensibilidad del detector tiene valores inválidos.";
    }

    if (!(zReview <= zAnomaly && zAnomaly <= zCritical)) {
      return "La sensibilidad debe cumplir el orden: baja ≤ normal ≤ alta.";
    }

    return null;
  };

  const validateExceptions = () => {
    const allowedTypes = exceptionTypes.map((type) => type.value);

    for (const row of exceptions) {
      if (!row.exception_date) {
        return "Todas las excepciones deben tener una fecha válida.";
      }

      if (!row.start_time || !row.end_time) {
        return "Todas las excepciones deben tener hora de inicio y fin.";
      }

      if (!row.exception_type || !allowedTypes.includes(row.exception_type)) {
        return "Todas las excepciones deben tener un tipo válido.";
      }

      if (row.start_time > row.end_time) {
        return `La excepción del ${row.exception_date} tiene horario de inicio posterior al de fin.`;
      }
    }

    return null;
  };

  const saveScheduleForScope = async (scopeValue) => {
    const filters = getScopeFilters(scopeValue);
    const currentRows = await getPassiveLoadSchedules(token, filters);
    const currentRowsArray = filterRowsByScope(currentRows, scopeValue);

    for (const row of scheduleGrid) {
      const existing = currentRowsArray.find(
        (item) => Number(item.day_of_week) === Number(row.day_of_week)
      );

      const payload = buildSchedulePayload(row, scopeValue);

      if (existing?.id) {
        await updatePassiveLoadSchedule(token, existing.id, payload);
      } else {
        await createPassiveLoadSchedule(token, payload);
      }
    }
  };

  const saveSettingsForScope = async (scopeValue) => {
    const filters = getScopeFilters(scopeValue);
    const currentSettings = await getPassiveLoadSettings(token, filters);
    const currentSettingsArray = filterRowsByScope(currentSettings, scopeValue);
    const existing = currentSettingsArray[0] || null;
    const payload = buildSettingsPayload(settingsForm, scopeValue);

    if (existing?.id) {
      await updatePassiveLoadSettings(token, existing.id, payload);
    } else {
      await createPassiveLoadSettings(token, payload);
    }
  };

  const saveExceptionsForScope = async (scopeValue) => {
    const filters = getScopeFilters(scopeValue);
    const currentExceptions = await getPassiveLoadExceptions(token, filters);
    const currentExceptionsArray = filterRowsByScope(
      currentExceptions,
      scopeValue
    );

    for (const row of exceptions) {
      const payload = buildExceptionPayload(row, scopeValue);

      const existing = currentExceptionsArray.find(
        (item) => item.id && Number(item.id) === Number(row.id)
      );

      if (existing?.id) {
        await updatePassiveLoadException(token, existing.id, payload);
      } else {
        await createPassiveLoadException(token, payload);
      }
    }
  };

  const handleSaveSchedule = async () => {
    setMessage(null);

    const scopeError = validateScope();
    if (scopeError) {
      showMessage("error", scopeError);
      return;
    }

    const error = validateSchedule();
    if (error) {
      showMessage("error", error);
      return;
    }

    setSavingSchedule(true);

    try {
      await saveScheduleForScope(scope);

      showMessage(
        "success",
        scope.mode === "branch"
          ? "Horario operativo guardado para la sucursal. Todos sus dispositivos usarán esta configuración al recalcular."
          : "Horario operativo específico guardado para el dispositivo."
      );

      await loadConfiguration(scope);
    } catch (error) {
      console.error("Error guardando horario operativo:", error);
      showMessage("error", "No se pudo guardar el horario operativo.");
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleSaveSettings = async () => {
    setMessage(null);

    const scopeError = validateScope();
    if (scopeError) {
      showMessage("error", scopeError);
      return;
    }

    const error = validateSettings();
    if (error) {
      showMessage("error", error);
      return;
    }

    setSavingSettings(true);

    try {
      await saveSettingsForScope(scope);

      showMessage(
        "success",
        scope.mode === "branch"
          ? "Parámetros guardados para la sucursal. Todos sus dispositivos usarán esta configuración al recalcular."
          : "Parámetros específicos guardados para el dispositivo."
      );

      await loadConfiguration(scope);
    } catch (error) {
      console.error("Error guardando parámetros:", error);
      showMessage("error", "No se pudieron guardar los parámetros.");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleAddException = () => {
    setExceptions((current) => [...current, createDefaultExceptionRow()]);

    showMessage(
      "info",
      "Se agregó una excepción. Recordá guardar para aplicar los cambios."
    );
  };

  const handleDeleteException = async (index) => {
    const exceptionToDelete = exceptions[index];
    if (!exceptionToDelete) return;

    if (exceptionToDelete.id) {
      try {
        await deletePassiveLoadException(token, exceptionToDelete.id);
        setExceptions((current) => current.filter((_, idx) => idx !== index));
        showMessage("success", "Excepción eliminada.");
        return;
      } catch (error) {
        console.error("Error eliminando excepción:", error);
        showMessage("error", "No se pudo eliminar la excepción.");
        return;
      }
    }

    setExceptions((current) => current.filter((_, idx) => idx !== index));
  };

  const handleSaveExceptions = async () => {
    setMessage(null);

    const scopeError = validateScope();
    if (scopeError) {
      showMessage("error", scopeError);
      return;
    }

    const error = validateExceptions();
    if (error) {
      showMessage("error", error);
      return;
    }

    setSavingExceptions(true);

    try {
      await saveExceptionsForScope(scope);

      showMessage(
        "success",
        scope.mode === "branch"
          ? "Excepciones guardadas para la sucursal. Todos sus dispositivos usarán esta configuración al recalcular."
          : "Excepciones específicas guardadas para el dispositivo."
      );

      await loadConfiguration(scope);
    } catch (error) {
      console.error("Error guardando excepciones:", error);
      showMessage("error", "No se pudieron guardar las excepciones.");
    } finally {
      setSavingExceptions(false);
    }
  };

  if (!normalizedDevices.length) {
    return (
      <div className="passive-config-overlay">
        <div className="passive-config-modal passive-config-modal--small">
          <div className="passive-config-header">
            <div>
              <h2>Configuración de carga pasiva</h2>
              <p>Seleccioná un dispositivo para identificar la sucursal.</p>
            </div>

            <button
              type="button"
              className="passive-config-close"
              onClick={onClose}
            >
              ×
            </button>
          </div>

          <div className="passive-config-empty">
            No se pudo identificar la sucursal de los dispositivos
            seleccionados.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="passive-config-overlay">
      <div className="passive-config-modal">
        <div className="passive-config-header">
          <div>
            <h2>Configuración de carga pasiva</h2>
            <p>
              Definí horarios operativos, excepciones y sensibilidad del
              algoritmo. Si seleccionás un solo dispositivo, se configura ese
              dispositivo. Si seleccionás varios, se configura la sucursal.
            </p>
          </div>

          <button
            type="button"
            className="passive-config-close"
            onClick={onClose}
            aria-label="Cerrar configuración"
          >
            ×
          </button>
        </div>

        <div className="passive-config-toolbar">
          <div className="passive-config-device">
            <label>
              {deviceSpecificMode
                ? "Sucursal del dispositivo"
                : "Sucursal a configurar"}
            </label>

            <select
              value={activeBranchKey || ""}
              onChange={(event) => {
                setActiveBranchKey(event.target.value);
                setMessage(null);
              }}
              disabled={deviceSpecificMode}
            >
              {branches.map((branch) => (
                <option key={branch.key} value={branch.key}>
                  {branch.label}
                </option>
              ))}
            </select>

            <small>
              {deviceSpecificMode
                ? "La sucursal se usa como referencia. Si el dispositivo no tiene configuración propia, se autocompleta desde esta sucursal."
                : "Esta configuración se aplicará a todos los dispositivos asociados a la sucursal seleccionada."}
            </small>
          </div>

          <label className="passive-config-checkbox passive-config-apply-all">
            <input
              type="checkbox"
              checked={deviceSpecificMode}
              onChange={(event) => {
                setDeviceModeTouched(true);
                setDeviceSpecificMode(event.target.checked);
                setMessage(null);
              }}
            />

            <span>
              {normalizedDevices.length === 1
                ? "Configurar este dispositivo específicamente"
                : "Configurar un dispositivo específico"}
            </span>
          </label>

          {deviceSpecificMode && (
            <div className="passive-config-device">
              <label>Dispositivo específico</label>

              <select
                value={activeDevice?.mac || ""}
                onChange={(event) => {
                  setActiveDeviceMac(event.target.value);
                  setMessage(null);
                }}
              >
                {normalizedDevices.map((device) => (
                  <option key={device.mac} value={device.mac}>
                    {device.deviceLabel} - {device.mac}
                  </option>
                ))}
              </select>

              <small>
                Si este dispositivo no tiene configuración propia, se completa
                con la configuración de su sucursal. Al guardar, se creará una
                configuración específica para este dispositivo.
              </small>
            </div>
          )}
        </div>

        {message && (
          <div
            className={`passive-config-message passive-config-message--${message.type}`}
          >
            {message.text}
          </div>
        )}

        <div className="passive-config-tabs">
          <button
            type="button"
            className={activeTab === "schedule" ? "active" : ""}
            onClick={() => setActiveTab("schedule")}
          >
            Horario operativo
          </button>

          <button
            type="button"
            className={activeTab === "settings" ? "active" : ""}
            onClick={() => setActiveTab("settings")}
          >
            Parámetros del algoritmo
          </button>

          <button
            type="button"
            className={activeTab === "exceptions" ? "active" : ""}
            onClick={() => setActiveTab("exceptions")}
          >
            Horas extra / excepciones
          </button>
        </div>

        {loading ? (
          <div className="passive-config-loading">
            Cargando configuración...
          </div>
        ) : (
          <>
            {activeTab === "schedule" && (
              <section className="passive-config-section">
                <div className="passive-config-section-header">
                  <div>
                    <h3>Horario operativo semanal</h3>
                    <p>
                      Indicá cuándo la instalación trabaja normalmente. Fuera de
                      estos horarios se analiza como período no productivo.
                    </p>

                    <div className="passive-config-help-box">
                      <strong>Alcance:</strong>{" "}
                      {scope.mode === "branch"
                        ? "esta configuración se aplica a toda la sucursal."
                        : "esta configuración se aplica solo al dispositivo seleccionado."}
                    </div>

                    <div className="passive-config-help-box">
                      <strong>Tip:</strong> usá “Pre-op.” y “Post-op.” para no
                      marcar como anomalía los arranques y apagados normales de
                      la instalación.
                    </div>
                  </div>

                  <div className="passive-config-actions">
                    <button
                      type="button"
                      className="heatmap-btn passive-btn-secondary"
                      onClick={loadStandardSchedule}
                    >
                      Cargar horario estándar
                    </button>

                    <button
                      type="button"
                      className="heatmap-btn"
                      onClick={handleSaveSchedule}
                      disabled={savingSchedule}
                    >
                      {savingSchedule ? "Guardando..." : "Guardar horario"}
                    </button>
                  </div>
                </div>

                <div className="passive-config-table-wrap">
                  <table className="passive-config-table">
                    <thead>
                      <tr>
                        <th>Día</th>
                        <th>Día con actividad</th>
                        <th>Inicio actividad</th>
                        <th>Fin actividad</th>
                        <th>Pre-op.</th>
                        <th>Post-op.</th>
                        <th>Nivel esperado</th>
                        <th>Descripción</th>
                        <th>Habilitar</th>
                      </tr>
                    </thead>

                    <tbody>
                      {scheduleGrid.map((row) => (
                        <tr key={row.day_of_week}>
                          <td className="passive-config-day">
                            {dayNames[row.day_of_week]}
                          </td>

                          <td>
                            <input
                              type="checkbox"
                              checked={Boolean(row.is_working_day)}
                              onChange={(event) =>
                                handleScheduleChange(
                                  row.day_of_week,
                                  "is_working_day",
                                  event.target.checked
                                )
                              }
                            />
                          </td>

                          <td>
                            <input
                              type="time"
                              value={row.start_time || ""}
                              disabled={!row.is_working_day}
                              onChange={(event) =>
                                handleScheduleChange(
                                  row.day_of_week,
                                  "start_time",
                                  event.target.value
                                )
                              }
                            />
                          </td>

                          <td>
                            <input
                              type="time"
                              value={row.end_time || ""}
                              disabled={!row.is_working_day}
                              onChange={(event) =>
                                handleScheduleChange(
                                  row.day_of_week,
                                  "end_time",
                                  event.target.value
                                )
                              }
                            />
                          </td>

                          <td>
                            <input
                              type="number"
                              min="0"
                              value={row.pre_operational_minutes}
                              onChange={(event) =>
                                handleScheduleChange(
                                  row.day_of_week,
                                  "pre_operational_minutes",
                                  event.target.value
                                )
                              }
                            />
                          </td>

                          <td>
                            <input
                              type="number"
                              min="0"
                              value={row.post_operational_minutes}
                              onChange={(event) =>
                                handleScheduleChange(
                                  row.day_of_week,
                                  "post_operational_minutes",
                                  event.target.value
                                )
                              }
                            />
                          </td>

                          <td>
                            <select
                              value={row.expected_activity_level || ""}
                              onChange={(event) =>
                                handleScheduleChange(
                                  row.day_of_week,
                                  "expected_activity_level",
                                  event.target.value
                                )
                              }
                            >
                              {activityLevels.map((level) => (
                                <option key={level.value} value={level.value}>
                                  {level.label}
                                </option>
                              ))}
                            </select>
                          </td>

                          <td>
                            <input
                              type="text"
                              value={row.description || ""}
                              onChange={(event) =>
                                handleScheduleChange(
                                  row.day_of_week,
                                  "description",
                                  event.target.value
                                )
                              }
                            />
                          </td>

                          <td>
                            <input
                              type="checkbox"
                              checked={Boolean(row.enabled)}
                              onChange={(event) =>
                                handleScheduleChange(
                                  row.day_of_week,
                                  "enabled",
                                  event.target.checked
                                )
                              }
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {activeTab === "settings" && (
              <section className="passive-config-section">
                <div className="passive-config-section-header">
                  <div>
                    <h3>Parámetros del algoritmo</h3>
                    <p>
                      Ajustá cómo se calcula la base normal de consumo y qué tan
                      sensible será el detector.
                    </p>

                    <div className="passive-config-help-box">
                      <strong>Alcance:</strong>{" "}
                      {scope.mode === "branch"
                        ? "estos parámetros se aplican a toda la sucursal."
                        : "estos parámetros se aplican solo al dispositivo seleccionado."}
                    </div>
                  </div>

                  <div className="passive-config-actions">
                    <button
                      type="button"
                      className="heatmap-btn passive-btn-secondary"
                      onClick={loadDefaultSettings}
                    >
                      Cargar valores recomendados
                    </button>

                    <button
                      type="button"
                      className="heatmap-btn"
                      onClick={handleSaveSettings}
                      disabled={savingSettings}
                    >
                      {savingSettings ? "Guardando..." : "Guardar parámetros"}
                    </button>
                  </div>
                </div>

                <div className="passive-settings-grid">
                  <div className="passive-settings-card">
                    <h4>Línea base</h4>
                    <p>
                      Define cómo se estima el consumo normal mínimo de cada
                      dispositivo. El valor real se calcula con los datos
                      históricos de ese dispositivo, no es fijo.
                    </p>

                    <label>
                      Referencia de consumo
                      <select
                        value={baseReferenceValue}
                        onChange={(event) =>
                          handleBaseReferenceChange(event.target.value)
                        }
                      >
                        <option value="low">
                          {baseReferenceProfiles.low.label}
                        </option>
                        <option value="recommended">
                          {baseReferenceProfiles.recommended.label}
                        </option>
                        <option value="stable">
                          {baseReferenceProfiles.stable.label}
                        </option>
                        {baseReferenceValue === "custom" && (
                          <option value="custom">
                            Personalizada - configuración existente
                          </option>
                        )}
                      </select>
                    </label>

                    <div className="passive-config-help-box">
                      {baseReferenceValue === "custom"
                        ? "Esta configuración tiene un valor personalizado. Podés elegir una referencia simple para reemplazarla."
                        : baseReferenceProfiles[baseReferenceValue]?.description}
                    </div>

                    <small>
                      <strong>Base mínima habitual:</strong> toma una referencia
                      más baja del consumo normal. Detecta más fácil consumos
                      elevados.
                    </small>

                    <small>
                      <strong>Base recomendada:</strong> es el equilibrio
                      sugerido. Usa una base baja habitual, pero evita tomar
                      casos demasiado extremos.
                    </small>

                    <small>
                      <strong>Base más estable:</strong> toma una referencia más
                      amplia. Puede generar menos avisos, porque considera más
                      consumos como normales.
                    </small>

                    <label>
                      Margen permitido
                      <select
                        value={baseToleranceValue}
                        onChange={(event) =>
                          handleBaseToleranceChange(event.target.value)
                        }
                      >
                        <option value="strict">
                          {baseToleranceProfiles.strict.label}
                        </option>
                        <option value="normal">
                          {baseToleranceProfiles.normal.label}
                        </option>
                        <option value="flexible">
                          {baseToleranceProfiles.flexible.label}
                        </option>
                        {baseToleranceValue === "custom" && (
                          <option value="custom">
                            Personalizado - configuración existente
                          </option>
                        )}
                      </select>
                    </label>

                    <div className="passive-config-help-box">
                      {baseToleranceValue === "custom"
                        ? "Esta configuración tiene un margen personalizado. Podés elegir Ajustado, Normal o Amplio para simplificarlo."
                        : baseToleranceProfiles[baseToleranceValue]?.description}
                    </div>

                    <small>
                      Ejemplo: si un dispositivo normalmente queda cerca de 0.10
                      kWh fuera de horario y otro cerca de 0.80 kWh, cada
                      dispositivo tendrá su propia línea base según sus datos.
                    </small>
                  </div>

                  <div className="passive-settings-card">
                    <h4>Sensibilidad del detector</h4>
                    <p>
                      Define qué tan rápido el sistema avisa cuando el consumo
                      fuera de horario se aleja de lo normal para ese
                      dispositivo.
                    </p>

                    <label>
                      Nivel de sensibilidad
                      <select
                        value={sensitivityProfileValue}
                        onChange={(event) =>
                          handleSensitivityProfileChange(event.target.value)
                        }
                      >
                        <option value="low">
                          {sensitivityProfiles.low.label}
                        </option>
                        <option value="normal">
                          {sensitivityProfiles.normal.label}
                        </option>
                        <option value="high">
                          {sensitivityProfiles.high.label}
                        </option>
                        {sensitivityProfileValue === "custom" && (
                          <option value="custom">
                            Personalizada - configuración existente
                          </option>
                        )}
                      </select>
                    </label>

                    <div className="passive-config-help-box">
                      {sensitivityProfileValue === "custom"
                        ? "Esta configuración tiene valores personalizados. Podés elegir Baja, Normal o Alta para simplificarla."
                        : sensitivityProfiles[sensitivityProfileValue]?.description}
                    </div>

                    <small>
                      Baja = menos avisos. Normal = recomendado. Alta = más
                      avisos.
                    </small>

                    <small>
                      Ejemplo: con sensibilidad alta se avisa ante cambios más
                      chicos. Con sensibilidad baja se avisa solo cuando el
                      consumo se aleja mucho de lo normal.
                    </small>
                  </div>
                </div>
              </section>
            )}

            {activeTab === "exceptions" && (
              <section className="passive-config-section">
                <div className="passive-config-section-header">
                  <div>
                    <h3>Horas extra / excepciones</h3>
                    <p>
                      Definí horas extra autorizadas, feriados o cierres
                      especiales. Las horas extra no se analizan como carga
                      pasiva durante ese período.
                    </p>

                    <div className="passive-config-help-box">
                      <strong>Alcance:</strong>{" "}
                      {scope.mode === "branch"
                        ? "estas excepciones se aplican a toda la sucursal."
                        : "estas excepciones se aplican solo al dispositivo seleccionado."}
                    </div>
                  </div>

                  <div className="passive-config-actions">
                    <button
                      type="button"
                      className="heatmap-btn passive-btn-secondary"
                      onClick={handleAddException}
                    >
                      Nueva excepción
                    </button>

                    <button
                      type="button"
                      className="heatmap-btn"
                      onClick={handleSaveExceptions}
                      disabled={savingExceptions}
                    >
                      {savingExceptions
                        ? "Guardando..."
                        : "Guardar excepciones"}
                    </button>
                  </div>
                </div>

                <div className="passive-config-table-wrap">
                  <table className="passive-config-table">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Inicio</th>
                        <th>Fin</th>
                        <th>Tipo</th>
                        <th>Descripción</th>
                        <th>Habilitar</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>

                    <tbody>
                      {exceptions.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="passive-config-empty">
                            No hay excepciones configuradas para este alcance.
                          </td>
                        </tr>
                      ) : (
                        exceptions.map((row, index) => (
                          <tr key={row.id ?? `new-${index}`}>
                            <td>
                              <input
                                type="date"
                                value={row.exception_date || ""}
                                onChange={(event) =>
                                  handleExceptionChange(
                                    index,
                                    "exception_date",
                                    event.target.value
                                  )
                                }
                              />
                            </td>

                            <td>
                              <input
                                type="time"
                                value={row.start_time || ""}
                                onChange={(event) =>
                                  handleExceptionChange(
                                    index,
                                    "start_time",
                                    event.target.value
                                  )
                                }
                              />
                            </td>

                            <td>
                              <input
                                type="time"
                                value={row.end_time || ""}
                                onChange={(event) =>
                                  handleExceptionChange(
                                    index,
                                    "end_time",
                                    event.target.value
                                  )
                                }
                              />
                            </td>

                            <td>
                              <select
                                value={row.exception_type || "OVERTIME"}
                                onChange={(event) =>
                                  handleExceptionChange(
                                    index,
                                    "exception_type",
                                    event.target.value
                                  )
                                }
                              >
                                {exceptionTypes.map((type) => (
                                  <option key={type.value} value={type.value}>
                                    {type.label}
                                  </option>
                                ))}
                              </select>
                            </td>

                            <td>
                              <input
                                type="text"
                                value={row.description || ""}
                                onChange={(event) =>
                                  handleExceptionChange(
                                    index,
                                    "description",
                                    event.target.value
                                  )
                                }
                              />
                            </td>

                            <td>
                              <input
                                type="checkbox"
                                checked={Boolean(row.enabled)}
                                onChange={(event) =>
                                  handleExceptionChange(
                                    index,
                                    "enabled",
                                    event.target.checked
                                  )
                                }
                              />
                            </td>

                            <td>
                              <button
                                type="button"
                                className="heatmap-btn passive-btn-secondary"
                                onClick={() => handleDeleteException(index)}
                              >
                                Eliminar
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PassiveLoadConfig;